import React from 'react';
import { Button, Alert, Avatar } from 'antd';
import { VideoCameraFilled, SoundFilled } from '@ant-design/icons';
import styled from '@emotion/styled';
import { StyledModal } from '../Modals/StyledModal';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { useBookingFlowControl } from '../../hooks/useBookingFlowControl';
import { useBookingFlowData } from '../../hooks/useBookingFlowData';
import BookingSummary from './BookingSummary';
import useUser from 'vl-core/hooks/useUser';
import useConfirmAppointmentStrategy from '../../hooks/useConfirmAppointmentStrategy';

const InfoList = styled.ul`
  padding-left: 16px;
  color: var(--productGray);
`;

const CallTypeContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 32px 0;
  gap: 16px;
  flex-direction: column;

  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const CallButton = styled(Button)<{ selected?: boolean }>`
  flex-grow: 1;
  height: 70px;
  display: flex;
  align-items: center;
  gap: 16px;
  justify-content: center;
  transition: all 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);

  ${({ selected }) => {
    const color = selected ? 'var(--productPrimary)' : 'var(--productGray)';

    return `
      && {
        border-color: ${color};
        color: var(--productPrimary);

        > div:not(.ant-btn-icon) {
          color: inherit !important;
        }
      }

      && .ant-avatar {
        background: #fff;
        border: 2px solid ${color};
        color: ${color};

        svg {
          fill: ${color};
        }
      }
    `;
  }}

  &:not(:disabled):hover {
    border-color: var(--productPrimary);
    color: var(--productPrimary);
    transition: border-color 0.2s cubic-bezier(0.645, 0.045, 0.355, 1);

    > div:not(.ant-btn-icon) {
      color: inherit !important;
    }

    .ant-avatar {
      border-color: var(--productPrimary);
      color: var(--productPrimary);

      svg {
        fill: var(--productPrimary);
      }
    }
  }

  &:disabled {
    border-color: var(--productGray);
    color: var(--productGray);

    > span:not(.ant-btn-icon) {
      color: var(--productGray);
    }

    .ant-avatar {
      border-color: var(--productGray);
      color: var(--productGray);

      svg {
        fill: var(--productGray);
      }
    }
  }
`;

type ConfirmAppointmentModalProps = {
  isVisible: boolean;
  onModalClose: () => void;
  onSuccessBooking: () => void;
};

const ConfirmAppointmentModal: React.FC<ConfirmAppointmentModalProps> = ({
  isVisible,
  onModalClose,
  onSuccessBooking
}) => {
  const { t } = useTranslation('common');
  const user = useUser();

  const { bookingData } = useBookingFlowData();
  const { updateAppointmentCallType } = useBookingFlowControl();
  const {
    confirm: confirmBooking,
    isPending: isLoading,
    isError,
    error,
    reset: resetError
  } = useConfirmAppointmentStrategy();

  const selectedCallType = bookingData?.callType;
  const clinicianData = bookingData?.clinician;
  const apptDate = bookingData?.apptDate;

  const onChangeCallType = (callType: 'AUDIO' | 'VIDEO') => () => {
    updateAppointmentCallType(callType);
  };

  const onCancelModal = () => {
    resetError();
    onModalClose();
  };

  const onConfirmAppointment = async () => {
    try {
      await confirmBooking();
      onSuccessBooking();
    } catch (err) {
      console.error('Error confirming appointment:', err);
    }
  };

  return (
    <StyledModal
      centered
      closable={!isLoading}
      open={isVisible}
      title={'What type of appointment would you like?'}
      data-testid="confirm-appointment-modal"
      onCancel={onCancelModal}
      onOk={onConfirmAppointment}
      cancelButtonProps={{ disabled: isLoading }}
      okButtonProps={{
        disabled: !selectedCallType,
        loading: isLoading,
        'data-testid': 'confirm-and-book-button'
      }}
      okText="Confirm and Book"
    >
      <InfoList>
        <li>{t('Choose a private, quiet space for your appointment.')}</li>
        <li>{t('For video appointments, you’ll need a phone or computer with a camera.')}</li>
        <li>{t('No special software is required.')}</li>
      </InfoList>
      {isError && <Alert message={error?.message} type="error" style={{ marginTop: 16 }} />}

      <CallTypeContainer>
        <CallButton
          size="large"
          disabled={isLoading}
          selected={selectedCallType === 'AUDIO'}
          onClick={onChangeCallType('AUDIO')}
          icon={<Avatar size={32} icon={<SoundFilled />} />}
          data-testid="audio-call-type-button"
        >
          {t('Phone appointment')}
        </CallButton>

        <CallButton
          size="large"
          disabled={isLoading}
          selected={selectedCallType === 'VIDEO'}
          onClick={onChangeCallType('VIDEO')}
          icon={<Avatar size={32} icon={<VideoCameraFilled />} />}
          data-testid="video-call-type-button"
        >
          {t('Video appointment')}
        </CallButton>
      </CallTypeContainer>
      {clinicianData && selectedCallType && apptDate && (
        <BookingSummary
          clinicianName={clinicianData.name}
          clinicianType={clinicianData.type}
          date={DateTime.fromJSDate(apptDate).setZone(user.timezone).toFormat('EEE dd MMM | HH:mm ZZZZ')}
          callType={selectedCallType}
        />
      )}
    </StyledModal>
  );
};

export default ConfirmAppointmentModal;
